import React, { useState } from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { Box, Collapse, Grid2 as Grid, SxProps, Menu } from '@mui/material';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import { useListContext } from '../../ListContext';
import { useDidUpdateEffect } from 'framework/utils';
import { useUiTheme } from 'framework/uiTheme';
import { Button } from '../../../ui/mui-builder/Button/Button';

interface ListFilterWrapperProps {
  filter?: React.ReactElement;
  actions?: React.ReactElement[];
  search?: React.ReactElement;
  sx?: SxProps;
  alwaysOpen?: boolean;
  useDropdown?: boolean;
}

const ListFilterWrapper = ({
  filter,
  search,
  actions,
  sx,
  alwaysOpen = false,
  useDropdown = false,
}: ListFilterWrapperProps) => {
  const { filter: listFilter = {}, loading, uncontrolledFilters } = useListContext();
  const { Button: ThemedButton = Button } = useUiTheme();

  const hasAnyFilters = Object.keys(listFilter).some(key => {
    if (uncontrolledFilters?.includes(key)) {
      return listFilter[key]?.eq === true;
    }
    return true;
  });

  const filters = { ...listFilter };

  uncontrolledFilters?.forEach(source => {
    delete filters[source];
  });

  const hasFilters = !isEmpty(filters);

  const [isOpen, setIsOpen] = useState(alwaysOpen || hasFilters);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  useDidUpdateEffect(() => {
    if (hasFilters && !isOpen) {
      setIsOpen(true);
    }
  }, [listFilter]);

  const toggleFiltersDisplaying = () => {
    setIsOpen(!isOpen);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  if (!filter && !search && (!actions || actions?.every((action: any) => !action))) {
    return null;
  }

  return (
    <>
      <Grid
        className="list-top-btns"
        container
        sx={[
          {
            marginBottom: '12px',
            pointerEvents: loading ? 'none' : 'auto',
            display: alwaysOpen ? 'none' : 'flex',
            '& .dropdown-toggle, & .create-btn': {
              color: 'text.primary',
              borderRadius: '2px',
              padding: '8px 14px',
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'action.hover' },
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <Grid
          className="list-top-btns__filters"
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
              order: 1,
              width: '100%',
            },
            '& .react-autosuggest__container': {
              marginRight: '10px',
              '&:last-child': { marginRight: 0 },
              [theme.breakpoints.down('sm')]: { width: '100%' },
            },
            '& .MuiFormControl-root': {
              margin: 0,
              '& ~ .react-autosuggest__suggestions-container': {
                transform: 'translateY(7px)',
                backgroundColor: 'common.white',
              },
            },
            '& .react-autosuggest__input': {
              [theme.breakpoints.down('sm')]: {
                minWidth: '180px !important',
                width: '100%',
              },
            },
          })}
        >
          {search}
          {!!filter && !alwaysOpen && (
            <>
              {useDropdown ? (
                <ThemedButton
                  className={classnames('list-filter-toggle', { open: hasAnyFilters || isMenuOpen })}
                  onClick={handleOpenMenu}
                  aria-haspopup="true"
                  aria-controls={isMenuOpen ? 'list-filter-menu' : undefined}
                  aria-expanded={isMenuOpen ? 'true' : undefined}
                  disabled={loading}
                  variant="text"
                  startIcon={<FilterIcon />}
                  sx={{
                    color: 'text.primary',
                    borderRadius: '2px',
                    padding: '8px 14px',
                    backgroundColor: hasAnyFilters || isMenuOpen ? 'background.primary' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  Filter
                </ThemedButton>
              ) : (
                <ThemedButton
                  className={classnames('list-filter-toggle', { open: isOpen || hasFilters })}
                  variant="text"
                  startIcon={<FilterIcon />}
                  onClick={toggleFiltersDisplaying}
                  sx={{
                    color: 'text.primary',
                    borderRadius: '2px',
                    padding: '8px 14px',
                    backgroundColor: isOpen || hasFilters ? 'background.primary' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  Filter
                </ThemedButton>
              )}
            </>
          )}
        </Grid>
        <Grid
          gap="10px"
          className="list-top-btns__actions"
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
            [theme.breakpoints.down('sm')]: {
              order: 0,
              marginLeft: 0,
              marginBottom: '10px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              '& a': { margin: 0 },
            },
          })}
        >
          {actions}
        </Grid>
      </Grid>
      {!!filter && (
        <>
          {useDropdown ? (
            <Menu
              id="list-filter-menu"
              open={isMenuOpen}
              onClose={handleCloseMenu}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: '280px',
                    maxWidth: '400px',
                    mt: 1,
                  },
                },
              }}
            >
              <Box sx={{ padding: '8px 0' }}>{filter}</Box>
            </Menu>
          ) : (
            <Collapse in={alwaysOpen || isOpen} timeout={400} sx={{ marginTop: '10px' }}>
              <Box sx={{ paddingTop: '3px', paddingBottom: '5px' }}>
                {alwaysOpen && filter ? React.cloneElement(filter, { showAsIcon: true }) : filter}
              </Box>
            </Collapse>
          )}
        </>
      )}
    </>
  );
};

export default ListFilterWrapper;
